import React, { useState, useEffect, useCallback } from 'react';
import { Plus, CheckCircle2, Clock, Trash2, X, Calendar } from 'lucide-react';
import { followUps as followUpsApi } from '../../../services/api';

// ── Constants ──────────────────────────────────────────────────────────────────
const PRIORITY_BADGE = {
  high:   'bg-danger',
  normal: 'bg-primary',
  low:    'bg-secondary',
};

const STATUS_BADGE = {
  open:      'bg-success',
  completed: 'bg-dark',
  snoozed:   'bg-warning text-dark',
};

// ── Helpers ───────────────────────────────────────────────────────────────────
function isOverdue(fu) {
  if (fu.status !== 'open') return false;
  const today = new Date().toISOString().slice(0, 10);
  return fu.dueDate < today;
}

function fmtDate(d) {
  if (!d) return '—';
  // Append T00:00:00 to avoid timezone shifts when parsing date-only strings
  return new Date(d + 'T00:00:00').toLocaleDateString('en-US', {
    year: 'numeric', month: 'short', day: 'numeric',
  });
}

function todayStr() {
  return new Date().toISOString().slice(0, 10);
}

// ── Component ─────────────────────────────────────────────────────────────────
export default function FollowUpsPanel({ customerID, currentUserID, currentUserRole }) {
  const [followUps,  setFollowUps]  = useState([]);
  const [loading,    setLoading]    = useState(true);
  const [error,      setError]      = useState('');

  // Add form
  const [showForm,      setShowForm]      = useState(false);
  const [formDate,      setFormDate]      = useState('');
  const [formPriority,  setFormPriority]  = useState('normal');
  const [formNote,      setFormNote]      = useState('');
  const [submitting,    setSubmitting]    = useState(false);

  // Snooze inline
  const [snoozeID,   setSnoozeID]   = useState(null);
  const [snoozeDate, setSnoozeDate] = useState('');
  const [snoozing,   setSnoozing]   = useState(false);

  // Per-item completing state (ID → bool)
  const [completing, setCompleting] = useState({});

  // Delete confirm modal
  const [deleteID,  setDeleteID]  = useState(null);
  const [deleting,  setDeleting]  = useState(false);

  // ── Load ────────────────────────────────────────────────────────────────────
  const loadFollowUps = useCallback(async () => {
    setLoading(true);
    try {
      const res = await followUpsApi.listForCustomer(customerID);
      setFollowUps(res.data.data || []);
    } catch {
      setError('Failed to load follow-ups.');
    } finally {
      setLoading(false);
    }
  }, [customerID]);

  useEffect(() => { loadFollowUps(); }, [loadFollowUps]);

  // ── Derived lists ────────────────────────────────────────────────────────────
  const openFollowUps   = followUps.filter(f => f.status === 'open');
  const closedFollowUps = followUps.filter(f => f.status !== 'open');

  // ── Permission helpers ───────────────────────────────────────────────────────
  const isAdmin    = currentUserRole === 'admin';
  const canAct     = (fu) => isAdmin || fu.repID === currentUserID;
  const canDelete  = (fu) => isAdmin || fu.repID === currentUserID;

  // ── Submit new follow-up ─────────────────────────────────────────────────────
  const handleSubmit = async () => {
    if (!formDate || !formNote.trim()) return;
    setSubmitting(true);
    setError('');
    try {
      const res = await followUpsApi.create(customerID, {
        dueDate:      formDate,
        priority:     formPriority,
        reminderNote: formNote.trim(),
      });
      setFollowUps(prev => [res.data.data, ...prev]);
      setFormDate('');
      setFormPriority('normal');
      setFormNote('');
      setShowForm(false);
    } catch (err) {
      setError(err.response?.data?.message || 'Failed to create follow-up.');
    } finally {
      setSubmitting(false);
    }
  };

  // ── Mark complete ────────────────────────────────────────────────────────────
  const handleComplete = async (fu) => {
    setCompleting(prev => ({ ...prev, [fu.followUpID]: true }));
    setError('');
    try {
      await followUpsApi.update(customerID, fu.followUpID, { status: 'completed' });
      setFollowUps(prev => prev.map(f =>
        f.followUpID === fu.followUpID
          ? { ...f, status: 'completed', completedAt: new Date().toISOString() }
          : f
      ));
    } catch (err) {
      setError(err.response?.data?.message || 'Failed to complete follow-up.');
    } finally {
      setCompleting(prev => { const n = { ...prev }; delete n[fu.followUpID]; return n; });
    }
  };

  // ── Snooze ───────────────────────────────────────────────────────────────────
  const handleSnooze = async () => {
    if (!snoozeDate) return;
    setSnoozing(true);
    setError('');
    try {
      await followUpsApi.update(customerID, snoozeID, { status: 'snoozed', snoozedUntil: snoozeDate });
      setFollowUps(prev => prev.map(f =>
        f.followUpID === snoozeID
          ? { ...f, status: 'snoozed', snoozedUntil: snoozeDate }
          : f
      ));
      setSnoozeID(null);
      setSnoozeDate('');
    } catch (err) {
      setError(err.response?.data?.message || 'Failed to snooze follow-up.');
    } finally {
      setSnoozing(false);
    }
  };

  // ── Reopen ───────────────────────────────────────────────────────────────────
  const handleReopen = async (fu) => {
    setError('');
    try {
      await followUpsApi.update(customerID, fu.followUpID, { status: 'open' });
      setFollowUps(prev => prev.map(f =>
        f.followUpID === fu.followUpID
          ? { ...f, status: 'open', completedAt: null, snoozedUntil: null }
          : f
      ));
    } catch (err) {
      setError(err.response?.data?.message || 'Failed to reopen follow-up.');
    }
  };

  // ── Delete ───────────────────────────────────────────────────────────────────
  const handleDelete = async () => {
    setDeleting(true);
    try {
      await followUpsApi.delete(customerID, deleteID);
      setFollowUps(prev => prev.filter(f => f.followUpID !== deleteID));
      setDeleteID(null);
    } catch (err) {
      setError(err.response?.data?.message || 'Failed to delete follow-up.');
    } finally {
      setDeleting(false);
    }
  };

  // ── Toggle add form ──────────────────────────────────────────────────────────
  const toggleForm = () => {
    setShowForm(f => !f);
    setFormDate('');
    setFormPriority('normal');
    setFormNote('');
    setError('');
  };

  // ── Render a single follow-up card ───────────────────────────────────────────
  const renderCard = (fu) => {
    const overdue       = isOverdue(fu);
    const isSnoozingThis = snoozeID === fu.followUpID;

    return (
      <div key={fu.followUpID} className="card mb-2">
        <div className="card-body py-2 px-3">
          <div className="d-flex align-items-start justify-content-between gap-2">
            {/* Left: badges + note */}
            <div className="flex-grow-1 min-width-0">
              <div className="d-flex align-items-center gap-2 flex-wrap mb-1">
                <span className={`badge ${PRIORITY_BADGE[fu.priority] || 'bg-secondary'} text-capitalize`}>
                  {fu.priority}
                </span>
                <span className={`badge ${STATUS_BADGE[fu.status] || 'bg-secondary'} text-capitalize`}>
                  {fu.status}
                </span>
                <small className={`d-flex align-items-center gap-1 ${overdue ? 'text-danger fw-semibold' : 'text-muted'}`}>
                  <Calendar size={12} />
                  {fmtDate(fu.dueDate)}
                  {overdue && ' · overdue'}
                </small>
                {fu.repName && (
                  <small className="text-muted">· {fu.repName}</small>
                )}
              </div>
              <p className="mb-0 small">{fu.reminderNote}</p>
              {fu.status === 'snoozed' && fu.snoozedUntil && (
                <small className="text-muted d-block mt-1">
                  <Clock size={11} className="me-1" />
                  Snoozed until {fmtDate(fu.snoozedUntil)}
                </small>
              )}
            </div>

            {/* Right: action buttons */}
            {canAct(fu) && (
              <div className="d-flex gap-1 flex-shrink-0">
                {fu.status === 'open' && (
                  <>
                    <button
                      className="btn btn-success btn-sm py-0 px-1"
                      title="Mark complete"
                      onClick={() => handleComplete(fu)}
                      disabled={!!completing[fu.followUpID]}
                    >
                      {completing[fu.followUpID]
                        ? <span className="spinner-border" style={{ width: 12, height: 12, borderWidth: 2 }} role="status" aria-hidden="true" />
                        : <CheckCircle2 size={13} />}
                    </button>
                    <button
                      className="btn btn-outline-warning btn-sm py-0 px-1"
                      title="Snooze"
                      onClick={() => { setSnoozeID(fu.followUpID); setSnoozeDate(''); }}
                    >
                      <Clock size={13} />
                    </button>
                  </>
                )}
                {fu.status !== 'open' && (
                  <button
                    className="btn btn-outline-secondary btn-sm py-0 px-2"
                    style={{ fontSize: '0.75rem' }}
                    title="Reopen"
                    onClick={() => handleReopen(fu)}
                  >
                    Reopen
                  </button>
                )}
                {canDelete(fu) && (
                  <button
                    className="btn btn-outline-danger btn-sm py-0 px-1"
                    title="Delete"
                    onClick={() => setDeleteID(fu.followUpID)}
                  >
                    <Trash2 size={13} />
                  </button>
                )}
              </div>
            )}
          </div>

          {/* Snooze inline date picker */}
          {isSnoozingThis && (
            <div className="d-flex align-items-center gap-2 mt-2 pt-2 border-top flex-wrap">
              <small className="text-muted fw-semibold">Snooze until:</small>
              <input
                type="date"
                className="form-control form-control-sm"
                style={{ maxWidth: 160 }}
                value={snoozeDate}
                min={todayStr()}
                onChange={e => setSnoozeDate(e.target.value)}
                autoFocus
              />
              <button
                className="btn btn-warning btn-sm py-0 px-2"
                onClick={handleSnooze}
                disabled={!snoozeDate || snoozing}
              >
                {snoozing
                  ? <span className="spinner-border spinner-border-sm" role="status" aria-hidden="true" />
                  : 'Snooze'}
              </button>
              <button
                className="btn btn-outline-secondary btn-sm py-0 px-2"
                onClick={() => { setSnoozeID(null); setSnoozeDate(''); }}
                disabled={snoozing}
              >
                <X size={12} />
              </button>
            </div>
          )}
        </div>
      </div>
    );
  };

  // ── Render ───────────────────────────────────────────────────────────────────
  return (
    <div>
      {/* Header */}
      <div className="d-flex align-items-center justify-content-between mb-3">
        <h5 className="mb-0">Follow-Ups</h5>
        <button
          className="btn btn-primary btn-sm d-flex align-items-center gap-1"
          onClick={toggleForm}
        >
          <Plus size={14} />
          Schedule Follow-Up
        </button>
      </div>

      {/* Error */}
      {error && (
        <div className="alert alert-danger py-2 d-flex align-items-center justify-content-between mb-3">
          <span>{error}</span>
          <button className="btn-close btn-sm" onClick={() => setError('')} aria-label="Dismiss" />
        </div>
      )}

      {/* Add follow-up form */}
      {showForm && (
        <div className="card mb-3">
          <div className="card-body">
            <div className="row g-3">
              <div className="col-sm-4">
                <label className="form-label">
                  Due Date <span className="text-danger">*</span>
                </label>
                <input
                  type="date"
                  className="form-control"
                  value={formDate}
                  min={todayStr()}
                  onChange={e => setFormDate(e.target.value)}
                  autoFocus
                />
              </div>
              <div className="col-sm-3">
                <label className="form-label">Priority</label>
                <select
                  className="form-select"
                  value={formPriority}
                  onChange={e => setFormPriority(e.target.value)}
                >
                  <option value="low">Low</option>
                  <option value="normal">Normal</option>
                  <option value="high">High</option>
                </select>
              </div>
              <div className="col-sm-5">
                <label className="form-label">
                  Note <span className="text-danger">*</span>
                </label>
                <input
                  type="text"
                  className="form-control"
                  value={formNote}
                  maxLength={500}
                  onChange={e => setFormNote(e.target.value)}
                  placeholder="Follow-up reminder…"
                />
              </div>
            </div>
            <div className="d-flex gap-2 mt-3">
              <button
                className="btn btn-primary btn-sm d-flex align-items-center gap-1"
                onClick={handleSubmit}
                disabled={submitting || !formDate || !formNote.trim()}
              >
                {submitting
                  ? <span className="spinner-border spinner-border-sm" role="status" aria-hidden="true" />
                  : <Calendar size={13} />}
                Schedule
              </button>
              <button
                className="btn btn-outline-secondary btn-sm"
                onClick={() => setShowForm(false)}
                disabled={submitting}
              >
                Cancel
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Loading */}
      {loading ? (
        <div className="text-center py-4">
          <div className="spinner-border spinner-border-sm text-secondary" role="status">
            <span className="visually-hidden">Loading…</span>
          </div>
        </div>
      ) : (
        <>
          {/* Open section */}
          <h6 className="text-muted fw-semibold mb-2 text-uppercase" style={{ fontSize: '0.7rem', letterSpacing: '0.05em' }}>
            Open
          </h6>
          {openFollowUps.length === 0 ? (
            <div className="text-center text-muted py-3 border rounded mb-3 small">
              No open follow-ups.
            </div>
          ) : (
            openFollowUps.map(renderCard)
          )}

          {/* Completed / Snoozed section */}
          {closedFollowUps.length > 0 && (
            <>
              <h6 className="text-muted fw-semibold mb-2 mt-4 text-uppercase" style={{ fontSize: '0.7rem', letterSpacing: '0.05em' }}>
                Completed / Snoozed
              </h6>
              {closedFollowUps.map(renderCard)}
            </>
          )}
        </>
      )}

      {/* Delete confirmation modal */}
      {deleteID && (
        <>
          <div
            className="modal fade show d-block"
            tabIndex="-1"
            role="dialog"
            aria-modal="true"
          >
            <div className="modal-dialog modal-dialog-centered modal-sm">
              <div className="modal-content">
                <div className="modal-body text-center py-4">
                  <Trash2 size={32} className="text-danger mb-2" />
                  <p className="mb-0 fw-semibold">Delete this follow-up?</p>
                  <p className="text-muted small mt-1 mb-0">This action cannot be undone.</p>
                </div>
                <div className="modal-footer justify-content-center border-0 pt-0">
                  <button
                    className="btn btn-outline-secondary btn-sm"
                    onClick={() => setDeleteID(null)}
                    disabled={deleting}
                  >
                    Cancel
                  </button>
                  <button
                    className="btn btn-danger btn-sm"
                    onClick={handleDelete}
                    disabled={deleting}
                  >
                    {deleting
                      ? <span className="spinner-border spinner-border-sm" role="status" aria-hidden="true" />
                      : 'Delete'}
                  </button>
                </div>
              </div>
            </div>
          </div>
          <div
            className="modal-backdrop fade show"
            onClick={() => { if (!deleting) setDeleteID(null); }}
          />
        </>
      )}
    </div>
  );
}
